<template>
  <dialog-scroll-lock
    content-class="fill-height" width="600"
    :value="true"
    @input="$emit('close')"
  >

    <error-dialog :status="uploadStatus" />
    <error-dialog :status="deleteMediaStatus" />

    <sign-in-dialog ref="sign-in" v-model="showSignIn">
      <template v-slot:subtitle>Please sign in to upload custom media</template>
    </sign-in-dialog>

    <input v-show="false" ref="upload" type="file" accept="audio/*" @click.stop />

    <v-card class="fill-height d-flex flex-column">
      <div class="d-flex align-center pa-3">
        <div class="title pl-1">Music</div>
        <v-spacer />
        <v-switch
          class="mt-0 mr-2"
          hide-details
          :input-value="isActive"
          @click.native.capture.stop.prevent="isActive && $emit('input', null)"
        />
        <v-btn small color="primary" @click="$emit('close')">Done</v-btn>
      </div>

      <v-divider />

      <div class="d-flex justify-center align-center text-center pa-3" style="height: 88px;">
        <audio-preview-btn
          v-if="selected"
          class="flex-shrink-1 overflow-hidden"
          color="primary" dark
          :name="stripUUID(selected.name)"
          :src="value"
        />
        <div v-else class="d-flex align-center font-italic grey--text">No music selected</div>
      </div>

      <v-divider />

      <div class="pa-2 pb-0 d-flex">
        <v-btn v-if="!noCustomMedia" color="primary" text :loading="promptUploadStatus.pending || uploadStatus.pending" @click="promptUpload">
          <v-icon class="mr-2" v-html="mdiUpload" />
          <span class="text-body-2" style="text-transform: none; font-weight: normal;">Upload your own<span class="hidden-xs-only"> music</span>…</span>
        </v-btn>
        <v-spacer />
        <v-btn text color="warning" :disabled="!value" @click="$emit('input', null)">
          <v-icon class="mr-2" v-html="mdiMusicOff" />
          <span class="text-body-2" style="text-transform: none; font-weight: normal;">Remove</span>
        </v-btn>
      </div>

      <v-row class="flex-grow-0 ma-0 px-2 py-1">
        <v-col class="pa-2" cols="5" sm="4">
          <v-select
            :items="tags"
            :menu-props="{ maxHeight: '400' }"
            dense outlined hide-details label="Genres / Moods"
            v-model="selectedTag"
          />
        </v-col>
        <v-col class="pa-2" cols="7" sm="8">
          <v-text-field clearable dense outlined hide-details label="Search" v-model="filter" />
        </v-col>
      </v-row>

      <template v-if="loadDefaultLibraryStatus.pending || loadUserLibraryStatus.pending">
        <v-progress-linear indeterminate />
        <v-spacer />
      </template>

      <v-virtual-scroll v-else class="flex-grow-1" :items="filtered" :item-height="52">
        <template v-slot:default="{ item }">
          <v-list-item
            v-blur
            class="px-3"
            :input-value="value == item.url"
            @click="$emit('input', value == item.url ? null : item.url)"
          >
            <v-btn icon color="primary">
              <v-icon v-html="value == item.url ? mdiCheckboxMarked : mdiCheckboxBlankOutline" />
            </v-btn>
            <div class="ma-2 btn-group">
              <audio-preview-btn
                v-bind:class="{ 'btn-group-right': !item.tags }"
                :color="value == item.url ? 'primary' : null"
                :name="stripUUID(item.name)"
                :src="item.url"
                @click.stop.prevent
              />
              <v-btn
                v-if="!item.tags"
                class="btn-group-left"
                rounded
                :color="value == item.url ? 'primary' : null"
                :loading="deleting === item"
                @click.stop.prevent="deleteMedia(item)"
              >
                <v-icon color="warning" v-html="mdiDelete" />
              </v-btn>
            </div>
          </v-list-item>
        </template>
      </v-virtual-scroll>

    </v-card>
  </dialog-scroll-lock>
</template>

<script>
import AudioPreviewBtn from '@/components/AudioPreviewBtn.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import stripUUID from '@/filters/strip-uuid.js';
import { mdiAccount, mdiCheckboxBlankOutline, mdiCheckboxMarked, mdiDelete, mdiMusicOff, mdiUpload } from '@mdi/js';


// To add new music and tag it to a particular genre, assemble it all into a directory named after that genre. The
// name of each file will be used as the name in the music library (sans extension). It's best to run the files
// through ffmpeg to ensure they're optimally compressed - they must be mp3 files. Next, run the
// 'update_music_library.py' script on that directory to automatically update it. This will also print out any
// duplicates that might need correcting if they are in fact two different songs with the same name. Then, after
// ensuring you won't be replacing any files, upload the contents of that directory to /music/ in S3.


export default {
  name: 'music-library-dialog',

  components: {
    AudioPreviewBtn,
    SignInDialog
  },

  props: [ 'value', 'isActive', 'noCustomMedia' ],

  data() {
    return {
      showSignIn: false,
      deleting: null,
      defaultLibrary: [],
      userLibrary: [],
      selectedTag: 'All',
      filter: '',
      loadDefaultLibraryStatus: this.$track('loadDefaultLibrary'),
      loadUserLibraryStatus: this.$track('loadUserLibrary'),
      promptUploadStatus: this.$track('promptUpload'),
      uploadStatus: this.$track('upload'),
      deleteMediaStatus: this.$track('deleteMedia')
    };
  },

  computed: {
    tags() {
      const tags = new Set();
      for (const song of this.defaultLibrary)
        for (const tag of song.tags)
          tags.add(tag);
      return [ 'All', 'Your uploads', ...Array.from(tags).sort() ];
    },

    userLibraryWithLower() {
      // Note that these records do not include 'tags'
      return this.userLibrary.map(({ name, url }) => ({ name, url, _lower: name.toLowerCase() }));
    },

    filtered() {
      let byTag;
      if (this.selectedTag == 'All')
        byTag = [ ...this.userLibraryWithLower, ...this.defaultLibrary ];
      else if (this.selectedTag == 'Your uploads')
        byTag = this.userLibraryWithLower;
      else
        byTag = this.defaultLibrary.filter(x => x.tags.includes(this.selectedTag));
      if (!this.filter)
        return byTag;
      const lower = this.filter.toLowerCase();
      return byTag.filter(x => x._lower.includes(lower));
    },

    selected() {
      const v = this.value;
      return v && (this.userLibraryWithLower.find(x => x.url == v) || this.defaultLibrary.find(x => x.url == v));
    }
  },

  created() {
    Object.assign(this, { stripUUID, mdiAccount, mdiCheckboxBlankOutline, mdiCheckboxMarked, mdiDelete, mdiMusicOff, mdiUpload });
    this.loadDefaultLibrary();
  },

  methods: {
    async loadDefaultLibrary() {
      const media = (await import('@/utils/media-library-music.json')).default;
      this.defaultLibrary = media.map(([ name, tags ]) => ({
        name,
        tags,
        url: `https://cdn.ellacard.com/music/${name}.mp3`,
        _lower: name.toLowerCase()
      }));
    },

    async loadUserLibrary() {
      this.userLibrary = [];
      if (!this.isSignedIn || this.noCustomMedia)
        return;
      this.userLibrary = await this.$call('list-user-media-library', { type: 'music' });
    },

    async promptUpload() {
      if (!this.isSignedIn) {
        await this.$refs['sign-in'].prompt();
        if (!this.isSignedIn)
          return;
      }

      const el = this.$refs.upload;
      el.onchange = e => {
        const file = e.target.files[0];
        if (file)
          this.upload(file);
        e.target.onchange = null;
        e.target.value = null;
      };
      el.click();
    },

    async upload(file) {
      if (file.size / 1024 / 1024 > 15)
        throw new VisibleError('File is too large (15 MB limit)');

      if (!file.type.startsWith('audio'))
        throw new VisibleError('File is not an audio file');

      const base = file.name.replace(/\.[^/.]+$/, '');
      let name = base;
      for (let i = 2; this.userLibrary.find(x => x.name == name); i++)
        name = base + ' ' + i;
      const url = await this.$call('upload-to-user-media-library', { name, type: 'music', file });
      const song = { name, url };

      // Just add this song to the user library instead of reloading everything
      this.userLibrary = [ ...this.userLibrary, song ].sort();

      this.$emit('input', url);
      await this.$nextTick();
      if (!this.filtered.find(x => x.url == url)) {
        this.selectedTag = 'Your uploads';
        this.filter = '';
      }
    },

    async deleteMedia(song) {
      this.deleting = song;
      try {
        const r = await this.$confirm({ title: 'Are you sure?', msg: 'Are you sure you want to delete this item?' });
        if (r == 'No')
          return false;
        await this.$call('delete-from-user-media-library', { type: 'music', name: song.name });
        this.userLibrary.splice(this.userLibrary.find(x => x.url == song.url), 1);
        if (song.url == this.value)
          this.$emit('input', null);
      } finally {
        this.deleting = null;
      }
    }
  },

  watch: {
    'global.profile.user_id': {
      immediate: true,
      handler() {
        this.loadUserLibrary();
      }
    }
  }
}
</script>
